import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { PaginationAndSorting, Supplier } from '../types/types';
import { captureSupabaseError } from '../lib/sentry';

export function useGetSuppliers(
  organisationId: string,
  {
    page,
    pageSize,
    search,
    sortBy = 'updated_at',
    sortDirection,
  }: PaginationAndSorting,
  effectiveReloadKey?: number
) {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!organisationId) {
      setSuppliers([]);
      setTotal(0);
      return;
    }

    let isCancelled = false;

    const fetchSuppliers = async () => {
      setLoading(true);
      setError(null);

      try {
        let query = supabase
          .from('suppliers')
          .select(
            `
              id,
              company_name,
              registration_number,
              primary_address_id!inner (
                country,  
                city,
                street,
                postal_code
              ),
              created_at,
              updated_at,
              imported,
              buyer_supplier_link!inner (
                buyer_org_id 
              )
            `,
            { count: 'exact' }
          )
          .eq('buyer_supplier_link.buyer_org_id', organisationId)
          .order(sortBy, { ascending: sortDirection === 'asc' })
          .range((page - 1) * pageSize, page * pageSize - 1);

        if (search) {
          query = query.or(
            `company_name.ilike.%${search}%,registration_number.ilike.%${search}%`
          );
        }

        console.log(
          'Fetching suppliers with organisationId:',
          organisationId,
          'page:',
          page,
          'pageSize:',
          pageSize,
          'search:',
          search
        );

        const { data, error, count } = await query;

        if (isCancelled) return;

        if (error) {
          setError(error.message);
          setSuppliers([]);
          setTotal(0);
        } else {
          const formatted = (data || []).map((s) => {
            const flags: string[] = [];
            if (s.imported) flags.push('imported');

            // ⚠️ Mapping address data correctly is crucial since it's nested
            const addressData = Array.isArray(s.primary_address_id)
              ? s.primary_address_id[0]
              : s.primary_address_id;

            return {
              ...s,
              country: addressData?.country, // Use country from the nested address object
              city: addressData?.city,
              flags,
            };
          });

          setSuppliers(formatted as unknown as Supplier[]);
          setTotal(count || 0);
        }
      } catch (err: unknown) {
        captureSupabaseError(err, { op: 'suppliers.fetch' });
        if (!isCancelled) {
          setError(err instanceof Error ? err.message : 'Unknown error');
          setSuppliers([]);
          setTotal(0);
        }
      } finally {
        if (!isCancelled) setLoading(false);
      }
    };

    fetchSuppliers();

    return () => {
      isCancelled = true;
    };
  }, [
    organisationId,
    page,
    pageSize,
    search,
    sortBy,
    sortDirection,
    effectiveReloadKey,
  ]);

  return { suppliers, total, loading, error };
}
